import { FunctionComponent, useRef } from 'react';
import { ApolloConsumer } from '@apollo/client';
import { toggleHeaderState } from 'queries/headerState/headerStateActions';
import usePromotionDimensions from 'hooks/usePromotionDimensions';
import {
  PromoImageHeadingStyle,
  PromoImageHeadingInnerWrapperStyle,
  PromoImageHeadingContentStyle,
  PromoImageHeadingButtonsContainerStyle,
  PromoImageAlphaLayer
} from './PromoImageHeading.style';
import { BodyText, Heading1 } from '../Text/Text.style';
import { Grid, GridElement } from '../../styles/grid';
import Bookmark from '../Bookmark';
import {
  CLIENT,
  ICONS,
  IMAGE_MEDIA_TYPE,
  TEALIUM_EVENT_CATEGORY,
  TEALIUM_EVENT_LABEL,
  TEALIUM_EVENT_NAME
} from '../../constants';
import Link from '../Link';
import { TextLabelStyle } from '../VideoItem/VideoItemDescription/VideoItemDescription.style';
import ImageMedia from '../ImageMedia';

const PromoImageHeading: FunctionComponent<IPromoHeading> = ({
  imageMediaId,
  viewMode,
  isLive,
  title,
  description,
  noEpisodesInLatestSeason,
  path,
  promoGuid,
  metadata
}) => {
  const promoImageContainer = useRef<HTMLDivElement>(null);
  const { width, height } = usePromotionDimensions(promoImageContainer);

  return (
    <ApolloConsumer>
      {client => {
        if (!imageMediaId) {
          toggleHeaderState(client as any, { isTransparent: false });
        }
        return (
          <PromoImageHeadingStyle data-testid="moviePromoImage" hasImage={!!imageMediaId} ref={promoImageContainer}>
            {!!(height && imageMediaId) && (
              <PromoImageAlphaLayer>
                <ImageMedia
                  publicId={imageMediaId}
                  imageMedia={{ type: IMAGE_MEDIA_TYPE.REMOTE }}
                  shouldLazyLoad={false}
                  transformations={
                    viewMode === CLIENT.DESKTOP
                      ? {
                          width,
                          height,
                          crop: 'fill',
                          gravity: 'faces:auto',
                          fetch_format: 'auto',
                          aspect_ratio: '16:9'
                        }
                      : {
                          width: '1000',
                          crop: 'fill',
                          gravity: 'faces:auto',
                          fetch_format: 'auto',
                          aspect_ratio: '4:3'
                        }
                  }
                />
              </PromoImageAlphaLayer>
            )}
            <PromoImageHeadingInnerWrapperStyle isFilmsPage>
              <Grid>
                <GridElement smColumn="1 / 13" mdColumn="1 / 13" lgColumn="1 / 13">
                  <PromoImageHeadingContentStyle data-testid="promoHeadingContent" isImageValid={imageMediaId}>
                    {isLive && <TextLabelStyle>Live</TextLabelStyle>}
                    <Heading1 data-testid="promoHeadingTitle">{title}</Heading1>
                    {description && <BodyText data-testid="promoDescription">{description}</BodyText>}
                    <PromoImageHeadingButtonsContainerStyle data-testid="promoImageHeadingButtonsContainerStyle">
                      {!noEpisodesInLatestSeason && (
                        <Link
                          path={path}
                          clickEventValues={{
                            label: TEALIUM_EVENT_LABEL.HEADER_PROMO,
                            name: TEALIUM_EVENT_NAME.CONTENT_LISTING_CLICK,
                            category: TEALIUM_EVENT_CATEGORY.CONTENT_LISTING,
                            defaultValues: metadata
                          }}
                        >
                          <img src={ICONS.PLAY_FILL} alt="Play button" />
                          Kijk nu
                        </Link>
                      )}
                      <Bookmark
                        programGuid={promoGuid}
                        clickEventValues={{
                          name: TEALIUM_EVENT_NAME.ADD_TO_FAVORITE,
                          label: TEALIUM_EVENT_LABEL.HEADER_PROMO,
                          category: TEALIUM_EVENT_CATEGORY.CONTENT_LISTING,
                          defaultValues: metadata
                        }}
                      />
                    </PromoImageHeadingButtonsContainerStyle>
                  </PromoImageHeadingContentStyle>
                </GridElement>
              </Grid>
            </PromoImageHeadingInnerWrapperStyle>
          </PromoImageHeadingStyle>
        );
      }}
    </ApolloConsumer>
  );
};

export default PromoImageHeading;
